<script lang="ts" setup>
  import { storeToRefs } from 'pinia'
  import { Ref } from 'vue'
  import useTranslation from '~/composables/Translation/Translation'
  import { useGameContentStore } from '~/store/GameContent'
  import useGameLocation from '~/composables/GameLocation/GameLocation'
  import { computed } from '#imports'
  import { useLocaleStore } from '~/store/Locale'
  import { GameInteractionCategory, GameInteractionId } from '~/model/GameInteraction/GameInteraction'
  import { useGameTaskStore } from '~/store/GameTask'
  import { GameTaskStoreEntry } from '~/model/GameTaskStore/GameTaskStore'
  import { useOverlayStore } from '~/store/Overlay'
  import { OverlayComponent, OverlayId } from '~/model/Overlay/Overlay'
  import { UiOverlayFacet } from '~/components/Ui/UiOverlay/UiOverlayFacet'
  import { useGameProgressStore } from '~/store/progress/GameProgress'
  import useGameTrouvaille from '~/composables/GameTrouvaille/GameTrouvaille'
  import { useGameStateStore } from '~/store/GameState'

  interface Props {
    interactionId: GameInteractionId
    category: GameInteractionCategory
  }

  const localeStore = useLocaleStore()
  const gameStateStore = useGameStateStore()
  const gameContentStore = useGameContentStore()
  const gameTaskStore = useGameTaskStore()
  const gameTaskStoreRefs = storeToRefs(gameTaskStore)

  const props = defineProps<Props>()
  const { localeId } = storeToRefs(localeStore)
  const { completedInteractionList } = storeToRefs(gameStateStore)
  const { locationContent, rewardList, taskList } = storeToRefs(gameContentStore)
  const { completeInteraction } = useGameProgressStore()
  const { openOverlay } = useOverlayStore()
  const { t } = useTranslation()
  const { toGameLocation } = useGameLocation()
  const { unlockTrouvailles } = useGameTrouvaille()

  const interactionCategory = computed<string>(() => {
    return t(`interaction-category.${props.category}`)
  })

  const isCompleted = computed<boolean>(() => {
    return completedInteractionList.value.has(props.interactionId)
  })
  const isAllowedToComplete = computed<boolean>(() => {
    if (!taskList.value || !rewardList.value || !locationContent.value) {
      return false
    }

    return taskList.value.every(({ id }) => {
      const store = gameTaskStoreRefs[id] as Ref<GameTaskStoreEntry>
      return store.value?.isCompleted
    })
  })

  const onRequestComplete = () => {
    if (!isAllowedToComplete.value) {
      return
    }

    completeInteraction(props.interactionId)
    unlockTrouvailles(...rewardList.value)

    openOverlay(OverlayId.Interstitital, {
      transition: 'trs-simple-fade',
      facets: [UiOverlayFacet.Interstitial],
      component: OverlayComponent.OverlayContentTaskComplete,
      disableScroll: true,
      props: {
        interactionId: props.interactionId,
        locationId: locationContent.value!.id,
      },
    })
  }
</script>

<template>
  <div class="c-interaction-complete-box">
    <div class="c-interaction-complete-box__box">
      <h2 class="c-interaction-complete-box__title">
        <template v-if="isCompleted">
          {{ t('interaction-complete-box.title-finished', { category: interactionCategory }) }}
        </template>
        <template v-else>
          {{ t('interaction-complete-box.title', { category: interactionCategory }) }}
        </template>
      </h2>
      <p class="c-interaction-complete-box__description">
        <template v-if="isCompleted">
          {{ t('interaction-complete-box.description-finished') }}
        </template>
        <template v-else-if="taskList.length === 0">
          {{ t('interaction-complete-box.description-no-tasks') }}
        </template>
        <template v-else>
          {{ t('interaction-complete-box.description') }}
        </template>
      </p>
      <div class="c-interaction-complete-box__actions">
        <ul class="c-interaction-complete-box__list u-reset">
          <li class="c-interaction-complete-box__entry">
            <NuxtLink
              v-if="locationContent"
              :to="toGameLocation(locationContent.id)"
              class="btn btn--primary btn--large"
            >
              {{ t('interaction-complete-box.action-back-label') }}
            </NuxtLink>
          </li>
          <li v-if="!isCompleted" class="c-interaction-complete-box__entry">
            <button
              :disabled="isAllowedToComplete ? null : true"
              class="btn btn--primary btn--large"
              @click="onRequestComplete"
            >
              {{ t('interaction-complete-box.action-complete-label') }}
            </button>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  @use 'sass:map';
  @use 'sass:math';
  @use '@nirazul/scss-utils' as utils;
  @use '/assets/scss/util/color/color' as col;
  @use '/assets/scss/base/typography/typography' as type;
  @use '/assets/scss/util/anaglyph/anaglyph' as anaglyph;

  $offset: 4px;

  .c-interaction-complete-box {
    grid-column: 4 / 13;
    margin: 0 $offset $offset 0;
  }

  .c-interaction-complete-box__box {
    @include anaglyph.with-shadow-apply(mono, $offset, $offset, 0, 0, col.$monochrome-lead);
    position: relative;
    width: 100%;
    padding: 28px 32px;
    color: col.$monochrome-black;
    background-color: col.$variant-cyan-light;
  }

  .c-interaction-complete-box__description {
    margin-top: 8px;
  }

  .c-interaction-complete-box__actions {
    margin-top: 8px;
  }

  .c-interaction-complete-box__actions {
    margin-top: 20px;
  }

  .c-interaction-complete-box__list {
    display: flex;
    flex-flow: row nowrap;
    justify-content: flex-end;
    width: 100%;
    gap: 32px;
  }
</style>
